/**
 * Pool-agnostic types + formatting shared by the Organic and Ads pool analytics tabs.
 * (Moved here from organic/organicMetrics.ts, which re-exports them unchanged.)
 */

export type WindowDays = 7 | 28 | 90;

export type CheckStatus = 'pass' | 'watch' | 'fail' | 'waiting';

export interface Check {
    key: string;
    label: string;
    target: string;
    status: CheckStatus;
    value: string;
}

/** One funnel step, the shape both get_organic_funnel and get_ads_pool_funnel return. */
export interface FunnelStep {
    step_order: number;
    step: string;
    n: number | null;
    n_attributed: number | null;
    value_cents: number | null;
    available: boolean;
    source: string;
}

export interface PoolHour {
    hour_et: number;
    claims: number;
    assigned: number;
    reused: number;
    blocked: number;
    bot_skipped: number;
    rate_limited: number;
    blocking_pct: number | null;
    mean_in_use: number;
    max_in_use: number;
}

/** get_tracking_pool_health(p_start, p_end, p_pool) — T-006, reused as is for pool='paid'. */
export interface PoolHealth {
    pool: string;
    pool_live: boolean;
    inventory: { number_type: string; status: string; numbers: number; junk_calls: number }[];
    hourly: PoolHour[];
    pool_size_active: number;
    exhaustion_events: number;
    /** Calls to this pool's "pool full" line(s) (site_phone_lines) in the window. */
    pool_full_calls?: number;
    timing: {
        matched_calls: number;
        unmatched_calls: number;
        junk_calls: number;
        delay_p50_min: number | null;
        delay_p90_min: number | null;
        calls_after_hold: number;
        window_minutes: number;
    };
}

/** PostgREST returns bigint/numeric as number or string; null stays null. */
export const num = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
};
export const n0 = (v: unknown): number => num(v) ?? 0;

export const formatMoney = (cents: unknown): string => {
    const c = n0(cents);
    const dollars = c / 100;
    if (Math.abs(dollars) >= 10000) return `$${(dollars / 1000).toFixed(1)}k`;
    return `$${Math.round(dollars).toLocaleString('en-CA')}`;
};

export const formatDelta = (cur: unknown, prev: unknown): string => {
    const c = n0(cur);
    const p = n0(prev);
    if (p === 0) return c === 0 ? 'no change' : 'new vs 0';
    const pct = ((c - p) / p) * 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(0)} % vs previous`;
};

// PostHog person link — same shape as NestJS web-lead-notification.service.ts
// (POSTHOG_PROJECT_URL default https://us.posthog.com/project/578876 + /person/<distinct_id>).
// The admin makes no PostHog API calls; this is a plain link out.
export const POSTHOG_PROJECT_URL = 'https://us.posthog.com/project/578876';
export const posthogPersonUrl = (distinctId: string | null | undefined): string | null =>
    distinctId ? `${POSTHOG_PROJECT_URL}/person/${encodeURIComponent(distinctId)}` : null;

export const isoDaysAgo = (days: number): string => new Date(Date.now() - days * 86400000).toISOString();
